import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-29";
const ANSWER =
  "Eine Shopify Theme-Anpassung wird meist nach Stunden abgerechnet. Bei Nicolai Schnepf sind es 110 € netto pro Stunde. Kleine Anpassungen wie Farben, Schriften oder ein neuer Abschnitt liegen oft zwischen 220 und 880 € netto. Größere Umbauten mit eigenen Funktionen werden vorab als Festpreis kalkuliert.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/was-kostet-eine-shopify-theme-anpassung";
const TITLE = "Was kostet eine Shopify Theme-Anpassung?";
const DESCRIPTION =
  "Was eine Theme-Anpassung bei Shopify kostet: typische Aufgaben, Stundensatz und wann sich ein Festpreis lohnt.";

const faqs = [
  {
    q: "Was kostet eine kleine Theme-Anpassung?",
    a: "Farben, Schriften, ein neuer Abschnitt oder ein angepasstes Layout liegen bei 110 € netto pro Stunde meist zwischen 220 und 880 € netto.",
  },
  {
    q: "Kann ich das Theme nicht selbst anpassen?",
    a: "Grundeinstellungen ja, über den Theme-Editor. Sobald es um eigenen Code, neue Funktionen oder saubere Darstellung auf allen Geräten geht, ist Fachwissen günstiger als Probieren.",
  },
  {
    q: "Geht eine Anpassung beim Update verloren?",
    a: "Nicht, wenn sie sauber umgesetzt ist. Anpassungen werden so gebaut, dass Theme-Updates weiterhin möglich bleiben.",
  },
  {
    q: "Lohnt sich ein neues Theme statt Anpassung?",
    a: "Wenn die Wunschliste lang ist, oft ja. Ein passendes Theme plus gezielte Anpassungen ist häufig günstiger als ein altes Theme komplett umzubauen.",
  },
];

export const Route = createFileRoute("/fragen/was-kostet-eine-shopify-theme-anpassung")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Kosten",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Was kostet eine Shopify Theme-Anpassung?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Kosten"
      title="Was kostet eine Shopify Theme-Anpassung?"
      intro="Kurz gesagt: 110 € netto pro Stunde. Kleine Anpassungen liegen meist zwischen 220 und 880 € netto, größere Umbauten werden als Festpreis kalkuliert."
      source="frage-theme-anpassung-kosten"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Typische Anpassungen und ihr Rahmen",
          p: [],
          list: [
            "Farben, Schriften, Abstände: oft 2 bis 4 Stunden",
            "Neuer Abschnitt oder angepasstes Layout: meist 4 bis 8 Stunden",
            "Eigene Funktionen wie Rechner, Konfiguratoren oder Filter: wird vorab als Festpreis kalkuliert",
          ],
        },
        {
          h: "So bleibt die Anpassung updatefest",
          p: [
            "Saubere Anpassungen werden so gebaut, dass Theme-Updates weiterhin möglich sind. Das vermeidet, dass Sie bei jedem Update erneut zahlen.",
          ],
        },
        {
          h: "Anpassen oder neues Theme",
          p: [
            "Bei einer langen Wunschliste ist ein passendes Theme plus gezielte Anpassungen oft günstiger als ein alter Umbau. Das prüfe ich vorab und sage Ihnen ehrlich, welcher Weg günstiger ist.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung im Detail" },
        { to: "/fragen/was-kostet-ein-shopify-freelancer", label: "Was kostet ein Shopify Freelancer?" },
        { to: "/preise", label: "Preise" },
      ]}
    />
  );
}
